'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import {
  client,
  CLIENTS,
  RIVIERWIJK_ENTITY_COUNT,
  RIVIERWIJK_SHAREHOLDERS,
  type Client,
  type ClientNr,
} from '../demo-data'
import { useVisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, HeaderStat } from './app-screen'
import styles from './organogram-visual.module.css'
import { useScenario } from './use-scenario'

/** The client the structure is drawn around, and the holdings that hold it (35 + 25 + 40 = 100%). */
const SUBJECT_NR: ClientNr = '10040'
const SUBJECT = client(SUBJECT_NR)

/** Each holding, with the entities it holds besides this one — its own dashed group in the chart. */
const HOLDINGS = RIVIERWIJK_SHAREHOLDERS.map((shareholder) => ({
  ...client(shareholder.nr),
  pct: shareholder.pct,
  siblings: (CLIENTS as readonly Client[]).filter((row) => row.parent === shareholder.nr),
}))

/*
 * One coordinate space, in percentages of the canvas, for both the boxes and the lines that join
 * them — so a connector cannot drift away from the box it points at when the card resizes, which
 * is what the old fixed-pixel drawing did as soon as the card was not the size it was drawn for.
 */
const COL_X = [17, 50, 83] // the three holdings, left to right
const BUS_Y = 36 // where the three connectors run across before dropping into the subject

/*
 * Lines start and end *inside* the boxes they join rather than at a guessed edge — the boxes are
 * sized by their own text, so any edge written here would be wrong at some card size, which is
 * why the connectors used to stop short. The boxes are opaque and sit above the drawing, so the
 * overshoot never shows.
 */
const INTO_HOLDING = 12
const INTO_SUBJECT = 60
const INTO_GROUP = 56

const BADGE_X = [40, 50, 60] // each share sits on its own connector, side by side above the subject

/**
 * The structure is whole in every frame; what moves is which shareholding is being named.
 *
 * It used to build outward from the client and strip back to it again, so a third of the loop was
 * one card floating in an empty panel labelled "1 entiteit" — under a heading promising a web of
 * holdings. That frame was what the walkthrough caught, and it is gone: nothing is ever added or
 * removed. Each beat lifts one holding's connector and its percentage instead, which is what
 * "deelnemingspercentages tussen bedrijven" actually claims.
 */
const BEAT_HOLDS = [1600, 1200, 1200, 1200] as const
const REST_BEAT = 0 // the whole structure, nothing singled out

/** Which holding's line is lit on each beat; beat 0 lights none. */
const TRACED_AT: readonly (number | null)[] = [null, 0, 1, 2]

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the holding being traced. */
function tracedHolding(beat: number | null): number | null {
  return TRACED_AT[beat ?? REST_BEAT] ?? null
}

/**
 * Structure chart — the company structure around one client, built outward from it: the client
 * first, then the lines up to the holdings that hold it with each one's share sitting on its own
 * line, and finally the other entities those holdings hold, grouped per holding.
 *
 * Faithful to the app's own chart: holdings across the top, the subject highlighted below them,
 * sibling entities in dashed groups beside it, and orthogonal connectors carrying the percentages.
 * `beat === null` (SSR, no-JS, reduced motion) renders the whole structure.
 */
export function OrganogramVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  // Everything is present in every frame; only the traced holding changes.
  const traced = tracedHolding(beat)
  const holdingsShown = true
  const siblingsShown = true
  const count = RIVIERWIJK_ENTITY_COUNT

  return (
    <AppUiVisual label={t.screen.organogram.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="organogram" className="flex h-full flex-col">
          <AppHeader
            title={t.screen.organogram.title}
            detail={SUBJECT.name}
            right={<HeaderStat value={count} label="entiteiten" />}
          />

          <div className={styles.canvas}>
            {/* One wrapper over the whole drawing — boxes and connectors together — so it can be
                centred in the panel without moving either of them relative to the other. */}
            <div className={styles.drawing}>
              {/* Connectors sit behind the boxes and share their coordinate space; a non-scaling
                stroke keeps every line 1px whatever the card's aspect ratio does to the grid. */}
              <svg className={styles.lines} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                {HOLDINGS.map((holding, index) => (
                  <path
                    key={holding.nr}
                    className={clsx(styles.line, holdingsShown && styles.lineOn)}
                    d={`M ${COL_X[index]} ${INTO_HOLDING} V ${BUS_Y} H ${BADGE_X[index]} V ${INTO_SUBJECT}`}
                    fill="none"
                    stroke="var(--au-border)"
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                {/* Branches down from where the trunk already reaches, so no segment is drawn twice —
                  two identical strokes on the same pixel read as one thicker, darker line. */}
                {HOLDINGS.map((holding, index) =>
                  holding.siblings.length === 0 ? null : (
                    <path
                      key={`s-${holding.nr}`}
                      className={clsx(styles.line, siblingsShown && styles.lineOn)}
                      d={`M ${COL_X[index]} ${BUS_Y} V ${INTO_GROUP}`}
                      fill="none"
                      stroke="var(--au-border)"
                      strokeWidth="1"
                      vectorEffect="non-scaling-stroke"
                    />
                  ),
                )}
              </svg>

              {HOLDINGS.map((holding, index) => (
                <div
                  key={holding.nr}
                  className={clsx(
                    styles.node,
                    styles.holding,
                    holdingsShown && styles.nodeOn,
                    traced === index && styles.nodeTraced,
                  )}
                  style={{ left: `${COL_X[index]}%` }}
                >
                  <span className={styles.nodeName}>{holding.short}</span>
                  <span className={styles.nodeNr}>{holding.nr}</span>
                </div>
              ))}

              {HOLDINGS.map((holding, index) => (
                <span
                  key={`p-${holding.nr}`}
                  className={clsx(
                    styles.badge,
                    holdingsShown && styles.badgeOn,
                    traced === index && styles.badgeTraced,
                  )}
                  style={{ left: `${BADGE_X[index]}%` }}
                >
                  {holding.pct}%
                </span>
              ))}

              {HOLDINGS.map((holding, index) =>
                holding.siblings.length === 0 ? null : (
                  <div
                    key={`g-${holding.nr}`}
                    className={clsx(styles.group, siblingsShown && styles.groupOn)}
                    style={{ left: `${COL_X[index]}%`, transitionDelay: `${index * 70}ms` }}
                  >
                    {holding.siblings.map((sibling) => (
                      <span key={sibling.nr} className={clsx(styles.sibling, traced === index && styles.siblingTraced)}>
                        <span className={styles.siblingHead}>
                          <span className={styles.siblingName}>{sibling.short}</span>
                        </span>
                        <span className={styles.nodeNr}>{sibling.nr}</span>
                      </span>
                    ))}
                  </div>
                ),
              )}

              {/* What a holding holds of the group under it, on the line that joins them — the
                  same place, and the same pill, as the shares running up to the subject. It used to
                  be printed inside each entity's own box, which put a percentage where a name and a
                  client number go and said it twice for a group of two. A subsidiary under a
                  holding is held outright, which is what makes this the same fact as the
                  percentages above, told one level lower. */}
              {HOLDINGS.map((holding, index) =>
                holding.siblings.length === 0 ? null : (
                  <span
                    key={`gp-${holding.nr}`}
                    className={clsx(styles.badge, styles.groupBadge, traced === index && styles.badgeOn)}
                    style={{ left: `${COL_X[index]}%` }}
                  >
                    100%
                  </span>
                ),
              )}

              <div className={clsx(styles.node, styles.subject)}>
                <span className={styles.nodeName}>{SUBJECT.short}</span>
                <span className={styles.nodeNr}>{SUBJECT.nr}</span>
              </div>
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
